import React from 'react';

/**
 * Enhanced Form Textarea Component with label, validation, and help text
 * 
 * @param {string} label - Field label
 * @param {string} name - Field name
 * @param {any} value - Field value
 * @param {function} onChange - Change handler
 * @param {number} rows - Number of rows
 * @param {boolean} required - Whether field is required
 * @param {string} placeholder - Placeholder text
 * @param {string} helpText - Help text below the field
 * @param {string} error - Error message to display
 * @param {boolean} disabled - Whether field is disabled
 * @param {string} className - Additional CSS classes
 * @param {object} style - Inline styles
 * @param {object} textareaProps - Additional textarea props
 */
const FormTextarea = ({
  label,
  name,
  value,
  onChange,
  rows = 4,
  required = false,
  placeholder,
  helpText,
  error,
  disabled = false,
  className = '',
  style = {},
  ...textareaProps
}) => {
  const fieldId = `textarea-${name}`;
  const hasError = !!error;

  const defaultTextareaStyle = {
    backgroundColor: 'var(--color-surface)',
    border: `2px solid ${hasError ? 'var(--color-error, #ef4444)' : 'var(--color-border)'}`,
    color: 'var(--color-text)',
    ...style
  };

  return (
    <div className={`mb-4 ${className}`}>
      {label && (
        <label 
          htmlFor={fieldId} 
          className="block text-gray-700 dark:text-gray-200 mb-2 font-medium text-sm"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <textarea
        id={fieldId}
        name={name}
        value={value || ''}
        onChange={onChange}
        rows={rows}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`w-full px-4 py-2.5 border rounded-lg theme-input focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none ${
          hasError ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        style={defaultTextareaStyle}
        {...textareaProps}
      />
      
      {error && (
        <p className="mt-1.5 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
      
      {helpText && !error && (
        <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}
    </div>
  );
};

export default FormTextarea;



